<template>
  <!-- 外层容器，用于包裹 SVG -->
  <div :style="wrapperStyle">
    <svg
        :width="width"
        :height="height"
        xmlns="http://www.w3.org/2000/svg"
    >
      <!-- 背景矩形 -->
      <rect width="100%" height="100%" rx="5" :fill="backgroundColor" />

      <!-- 背景图片 -->
      <image
          v-if="bgImageUrl"
          :href="bgImageUrl"
          x="0"
          y="0"
          width="100%"
          height="100%"
          preserveAspectRatio="none"
      />

      <!-- 自动换行 + 自适应字体的文字 -->
      <text
          :font-size="currentFontSize"
          :font-weight="fontWeight"
          :font-family="fontFamily"
          :fill="fillColor"
          :stroke="strokeColor"
          :stroke-width="strokeWidth"
          stroke-linejoin="round"
          paint-order="stroke fill"
          text-anchor="middle"
          dominant-baseline="middle"
          :x="width / 2"
      >
        <!-- 多行文字，每一行使用 tspan 控制 -->
        <tspan
            v-for="(line, i) in lines"
            :key="i"
            x="50%"
            :y="getLineY(i)"
        >
          {{ line }}
        </tspan>
      </text>
    </svg>
  </div>
</template>

<script>
export default {
  name: "SmartTextSvg",

  props: {
    /** 要展示的文本内容 */
    text: { type: String, default: "极讯科技" },
    width: { type: Number, default: 500 },
    height: { type: Number, default: 120 },

    /** 行列布局控制（决定字体大小与每行字数） */
    rowNumber: { type: String, default: 4 },

    /** 字体样式相关属性 */
    fontWeight: { type: [String, Number], default: 900 },
    fontFamily: {
      type: String,
      default: "Noto Sans SC, PingFang SC, Microsoft YaHei, sans-serif",
    },
    fillColor: { type: String, default: "#000" },   // 字体填充颜色
    strokeColor: { type: String, default: "#fff" }, // 字体描边颜色
    strokeWidth: { type: Number, default: 20 },     // 描边粗细

    /** 背景设置 */
    backgroundColor: { type: String, default: "transparent" }, // 背景色
    bgImageUrl: { type: Object, default: "" },                 // 背景图
  },

  data() {
    return {
      lines: [],            // 存放拆分后的每一行文字
      currentFontSize: 20,  // 当前字体大小（动态计算）
    };
  },

  computed: {
    /** 外层 div 的样式，使 SVG 居中显示 */
    wrapperStyle() {
      return {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        width: `${this.width}px`,
        height: `${this.height}px`,
        background: this.backgroundColor,
      };
    },

    /**
     * 布局映射表（不同 rowNumber 下的配置）
     * - size: 默认字体大小
     * - perLine: 每行最多字符数
     * - offsets: 每行在高度方向上的占比位置，用于垂直分布
     */
    layoutMap() {
      return {
        4: {
          size: 38,
          perLine: 8,
          offsets: {
            1: [0.5],
            2: [0.4, 0.75],
            3: [0.3, 0.55, 0.8],
          },
        },
        6: {
          size: 32,
          perLine: 8,
          offsets: {
            1: [0.5],
            2: [0.42, 0.72],
            3: [0.3, 0.55, 0.8],
          },
        },
        8: {
          size: 24,
          perLine: 8,
          offsets: {
            1: [0.5],
            2: [0.40, 0.70],
            3: [0.3, 0.55, 0.8],
          },
        },
        10: {
          size: 24,
          perLine: 6,
          offsets: {
            1: [0.5],
            2: [0.40, 0.70],
            3: [0.25, 0.51, 0.78],
          },
        },
      };
    },
  },

  watch: {
    text(){
      this.fitText()
    },
    rowNumber(){
      this.fitText()
    } ,
  },

  mounted() {
    // 初始化时计算一次文字布局
    this.fitText();
  },

  methods: {
    /** 获取当前布局配置 */
    getConfig() {
      return this.layoutMap[this.rowNumber] || this.layoutMap[10];
    },

    /**
     * 计算每一行文字的垂直位置（y 坐标）
     * @param {number} index 当前行的索引
     */
    getLineY(index) {
      const cfg = this.getConfig();
      const total = this.lines.length;
      const h = this.height;
      const offsets = cfg.offsets[total];

      if (offsets && offsets[index] !== undefined) {
        return h * offsets[index];
      }

      // 否则自动等分计算
      const lineHeight = this.currentFontSize * 1.2;
      const totalTextHeight = lineHeight * total;
      const space = (h - totalTextHeight) / (total + 1);
      return space * (index + 1) + lineHeight * index + lineHeight / 2;
    },

    /**
     * 自动拆分文字 & 动态调整字体大小
     * - 根据 rowNumber 确定每行字数上限
     * - 最多支持三行
     */
    fitText() {
      const cfg = this.getConfig();
      const txt = this.text.trim();
      const lines = [];

      // 按每行最大字符数拆分
      for (let i = 0; i < txt.length && lines.length < 3; i += cfg.perLine) {
        lines.push(txt.slice(i, i + cfg.perLine));
      }

      // 根据行数调整字体比例
      let fontSize = cfg.size;
      if (lines.length === 2) fontSize *= 0.9;
      if (lines.length === 3) fontSize *= 0.8;

      // 更新数据
      this.currentFontSize = Math.round(fontSize);
      this.lines = lines;
    },
  },
};
</script>

<style scoped>
svg {
  display: block;
}
</style>
